import { useEffect, useMemo, useState } from 'react'

import { cloneDeep, isNullable } from '@app/utils'

import { getOrder, getSortingParameters, getInitialSortingParameters, getColumnClassName } from './utils'

import type { Column } from '@app/components/base/Table/types'
import type { SortingParametersMap, UseSortingParametersParams } from './types'

export const useSortingParameters = <ColumnType>({
  columns: rawColumns,
  sortingScheme
}: UseSortingParametersParams<ColumnType>): { columns: Array<Column<ColumnType>>, sortingParameters: string[] } => {
  const [columnsWithSorting, setColumnsWithSorting] = useState(rawColumns)

  const [sortingParametersMap, setSortingParametersMap] = useState<SortingParametersMap<ColumnType>>(
    getInitialSortingParameters(sortingScheme)
  )

  const sortingParameters = useMemo(() => getSortingParameters(sortingParametersMap), [sortingParametersMap])

  useEffect(() => {
    const columns = cloneDeep(rawColumns)

    for (const column of columns) {
      const columnKey = column.key as keyof ColumnType
      const sortingParameter = sortingScheme[columnKey]

      if (isNullable(sortingParameter)) continue

      const sortingInfo = sortingParametersMap.get(columnKey)

      column.sorter = true
      column.onHeaderCell = (column: Column<ColumnType>) => {
        const columnKey = column.key as keyof ColumnType

        return {
          className: sortingInfo?.columnClassName,
          onClick: () => {
            setSortingParametersMap(prevSortingParametersMap => {
              const sortingInfo = prevSortingParametersMap.get(columnKey)

              if (isNullable(sortingInfo)) return prevSortingParametersMap

              const { order, sortingParameter } = sortingInfo

              const newOrder = getOrder(order)
              const newColumnClassName = getColumnClassName(newOrder)

              prevSortingParametersMap.set(columnKey, {
                order: newOrder,
                columnClassName: newColumnClassName,
                sortingParameter
              })

              return new Map(prevSortingParametersMap)
            })
          }
        }
      }
    }

    setColumnsWithSorting(columns)
  }, [rawColumns, sortingParametersMap])

  return { columns: columnsWithSorting, sortingParameters }
}
